import React, { useState } from 'react';
import { Language } from '../types';
import { t } from '../locales';

interface LoginViewProps {
  onLogin: (lang: Language) => void;
  initialLang: Language;
}

type AuthMode = 'login' | 'register' | 'verify';

const LoginView: React.FC<LoginViewProps> = ({ onLogin, initialLang }) => {
  const [selectedLang, setSelectedLang] = useState<Language>(initialLang);
  const [mode, setMode] = useState<AuthMode>('login');
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  // Simulate sending code
  const sendVerificationCode = () => {
    alert(t(selectedLang, 'codeSentAlert'));
    setMode('verify');
    setError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (mode === 'login') {
        // Mock login success
        onLogin(selectedLang);
    } else if (mode === 'register') {
        // Proceed to verification
        sendVerificationCode();
    } else if (mode === 'verify') {
        // Mock validation
        if (code === '1234') {
            onLogin(selectedLang);
        } else {
            setError(t(selectedLang, 'invalidCode'));
        }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-500 to-emerald-700 flex flex-col items-center justify-center p-6">
      <div className="bg-white dark:bg-gray-800 w-full max-w-sm rounded-2xl shadow-2xl p-8 relative overflow-hidden">
        
        <div className="flex justify-center mb-6">
           <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center text-white font-bold text-3xl shadow-lg">
                V
           </div>
        </div>
        
        <h1 className="text-2xl font-bold text-center text-gray-800 dark:text-white mb-8">
            {mode === 'verify' ? t(selectedLang, 'verifyEmail') : 'VitalEat'}
        </h1>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          
          {/* Language Selector (Only show on Login/Register screens) */}
          {mode !== 'verify' && (
            <div>
                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                {t(selectedLang, 'language')}
                </label>
                <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                    type="button" 
                    onClick={() => setSelectedLang('en')}
                    className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${selectedLang === 'en' ? 'bg-white dark:bg-gray-600 shadow-sm text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-300'}`}
                >
                    English
                </button>
                <button
                    type="button" 
                    onClick={() => setSelectedLang('fr')}
                    className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${selectedLang === 'fr' ? 'bg-white dark:bg-gray-600 shadow-sm text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-300'}`}
                >
                    Français
                </button>
                </div>
            </div>
          )}

          {/* Verification Mode */}
          {mode === 'verify' ? (
             <div className="flex flex-col gap-4 animate-slideIn">
                <p className="text-sm text-center text-gray-600 dark:text-gray-300">
                    {t(selectedLang, 'codeSentTo')} <span className="font-bold">{email}</span>
                </p>
                
                <div>
                    <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                        {t(selectedLang, 'enterCode')}
                    </label>
                    <input 
                        type="text" 
                        maxLength={4}
                        required
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        className="w-full p-3 text-center text-2xl tracking-[1em] font-mono rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                        placeholder="0000" 
                    />
                </div>
                
                {error && <p className="text-red-500 text-sm text-center font-medium">{error}</p>}
                
                <button 
                    type="button"
                    onClick={sendVerificationCode}
                    className="text-sm text-emerald-600 dark:text-emerald-400 hover:underline text-center"
                >
                    {t(selectedLang, 'resendCode')}
                </button>
             </div>
          ) : (
             /* Login / Register Mode */
             <>
                <div>
                    <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                    {t(selectedLang, 'email')}
                    </label>
                    <input 
                    type="email" 
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full p-3 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                    placeholder="user@example.com" 
                    />
                </div>

                <div>
                    <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                    {t(selectedLang, 'password')}
                    </label>
                    <input 
                    type="password" 
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full p-3 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                    placeholder="••••••••" 
                    />
                </div>
             </>
          )}

          {/* Main Action Button */}
          <button 
            type="submit"
            className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl shadow-lg hover:shadow-xl transition-all transform active:scale-95"
          >
            {mode === 'login' ? t(selectedLang, 'login') : mode === 'verify' ? t(selectedLang, 'verify') : t(selectedLang, 'signup')}
          </button>

          {/* Toggle Login/Register */}
          {mode !== 'verify' && (
              <div className="mt-2 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                      {mode === 'login' ? t(selectedLang, 'noAccount') : t(selectedLang, 'haveAccount')}
                      {' '}
                      <button 
                        type="button"
                        onClick={() => {
                            setMode(mode === 'login' ? 'register' : 'login');
                            setError('');
                        }}
                        className="font-bold text-emerald-600 dark:text-emerald-400 hover:underline"
                      >
                          {mode === 'login' ? t(selectedLang, 'signup') : t(selectedLang, 'login')}
                      </button>
                  </p>
              </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default LoginView;